import { IElement } from "core-integration/src/store/elements/types";
import { getNextNEvents } from "core-integration/src/store/events/reducers";
import { getFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { listTableCell } from "plos/src/styles/listTables.css";
import { vars } from "plos/src/styles/theme.css";
import { visuallyHidden } from "plos/src/styles/utils.css";
import { getAccessibleFixtureLabels } from "plos/src/utils/fixtures";
import { useSelector } from "react-redux";
import { fdrEventCount } from "../../hooks/useOverlayColumns";
import { getSuffixFromId, isElementFixtureHome } from "../../utils/fixtures";
import {
  difficultyCell,
  doubleEventGroup,
  fdrTableCell,
} from "./listViewFDRCell.css";

interface ListViewFDRCellsProps {
  element: IElement;
}

/**
 * Renders one list-view table cell per upcoming gameweek, each showing the
 * opponent name and H/A with FDR difficulty colouring. Double gameweeks render
 * both fixtures joined within the single gameweek cell to match the standalone FDR table.
 */
const ListViewFDRCells = ({ element }: ListViewFDRCellsProps) => {
  const upcomingEvents = useSelector(getNextNEvents(fdrEventCount));
  const fixturesByEvent = useSelector(getFixturesByEvent);
  const teamsById = useSelector(getTeamsById);

  return (
    <>
      {upcomingEvents.map((event) => {
        const allEventFixtures = fixturesByEvent[event.id] ?? [];
        const elementFixtures = allEventFixtures.filter(
          (f) => f.team_h === element.team || f.team_a === element.team,
        );
        const accessibleLabels = getAccessibleFixtureLabels(
          element,
          elementFixtures,
          teamsById,
        );
        const isDoubleEvent = elementFixtures.length > 1;

        return (
          <td className={listTableCell} key={event.id}>
            <div className={fdrTableCell}>
              {elementFixtures.length === 0 ? (
                <div>-</div>
              ) : (
                <div className={isDoubleEvent ? doubleEventGroup : undefined}>
                  {elementFixtures.map((fixture, index) => {
                    const isHome = isElementFixtureHome(
                      element.team,
                      fixture.team_h,
                    );
                    const opponentId = isHome ? fixture.team_a : fixture.team_h;
                    const difficulty =
                      (isHome
                        ? fixture.team_h_difficulty
                        : fixture.team_a_difficulty) ?? 0;
                    const rating =
                      difficulty as keyof typeof vars.colors.difficulties;
                    const suffix = getSuffixFromId(fixture.id, isHome);
                    const opponent = teamsById[opponentId];

                    return (
                      <div
                        key={fixture.id}
                        className={rating ? difficultyCell[rating] : undefined}
                      >
                        <span aria-hidden="true">
                          {opponent
                            ? `${opponent.short_name} ${suffix}`
                            : suffix}
                        </span>
                        <span className={visuallyHidden}>
                          {accessibleLabels[index]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </td>
        );
      })}
    </>
  );
};

export default ListViewFDRCells;
